// Página de resultado de pago (ePayco redirige aquí)
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, ArrowLeft } from 'lucide-react';

export function PaymentResultPage() {
    const [params] = useSearchParams();
    const [resolved, setResolved] = useState<{
        status: 'approved' | 'declined' | 'pending';
        reference: string;
        message: string;
    } | null>(null);

    useEffect(() => {
        const refPayco = params.get('ref_payco');
        const xResponse = (params.get('x_response') || params.get('x_transaction_state') || '').toLowerCase();
        const legacyState = params.get('transactionState') || params.get('state_pol') || '';
        const referenceCode =
            params.get('x_id_invoice') ||
            params.get('referenceCode') ||
            params.get('reference_sale') ||
            '';

        if (refPayco && !xResponse) {
            // Fetch validation reference from ePayco public endpoint
            fetch(`https://secure.epayco.co/validation/v1/reference/${refPayco}`)
                .then(r => r.json())
                .then(data => {
                    const d = data?.data || data || {};
                    const resp = String(d.x_response || d.x_transaction_state || '').toLowerCase();
                    const ref = String(d.x_id_invoice || d.x_ref_payco || refPayco);
                    if (resp.includes('aceptada') || resp.includes('approv')) {
                        setResolved({ status: 'approved', reference: ref, message: '' });
                    } else if (resp.includes('rechaz') || resp.includes('fallid') || resp.includes('declin')) {
                        setResolved({
                            status: 'declined',
                            reference: ref,
                            message: d.x_response_reason_text || '',
                        });
                    } else {
                        setResolved({ status: 'pending', reference: ref, message: '' });
                    }
                })
                .catch(() => {
                    setResolved({ status: 'pending', reference: refPayco, message: '' });
                });
            return;
        }

        if (
            xResponse.includes('aceptada') ||
            xResponse.includes('approv') ||
            legacyState === '4' ||
            legacyState === 'APPROVED'
        ) {
            setResolved({ status: 'approved', reference: referenceCode, message: '' });
        } else if (
            xResponse.includes('rechaz') ||
            xResponse.includes('fallid') ||
            legacyState === '6' ||
            legacyState === 'DECLINED'
        ) {
            setResolved({
                status: 'declined',
                reference: referenceCode,
                message: params.get('x_response_reason_text') || params.get('message') || '',
            });
        } else {
            setResolved({ status: 'pending', reference: referenceCode || refPayco || '', message: '' });
        }
    }, [params]);

    if (!resolved) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-300">
                Validando pago...
            </div>
        );
    }

    const isApproved = resolved.status === 'approved';
    const isDeclined = resolved.status === 'declined';

    const config = isApproved
        ? {
            icon: <CheckCircle2 className="w-16 h-16 text-emerald-400" />,
            title: '¡Pago exitoso!',
            description: 'Tu saldo ha sido recargado. La confirmación final llega por webhook de ePayco.',
            color: 'from-emerald-500/20 to-teal-500/10',
            border: 'border-emerald-500/30',
            badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
            badgeText: 'Aprobado',
        }
        : isDeclined
            ? {
                icon: <XCircle className="w-16 h-16 text-red-400" />,
                title: 'Pago rechazado',
                description: resolved.message || 'Tu pago no pudo ser procesado.',
                color: 'from-red-500/20 to-slate-800',
                border: 'border-red-500/30',
                badge: 'bg-red-500/20 text-red-300 border-red-500/30',
                badgeText: 'Rechazado',
            }
            : {
                icon: <Clock className="w-16 h-16 text-yellow-400" />,
                title: 'Pago en proceso',
                description: 'Tu pago está siendo validado. Te notificaremos cuando esté confirmado.',
                color: 'from-yellow-500/20 to-slate-800',
                border: 'border-yellow-500/30',
                badge: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
                badgeText: 'Pendiente',
            };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4">
            <div className="w-full max-w-md text-center">
                <div className={`bg-gradient-to-b ${config.color} border ${config.border} rounded-2xl p-8 space-y-6`}>
                    <div className="flex justify-center">{config.icon}</div>
                    <div>
                        <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${config.badge} mb-3`}>
                            {config.badgeText}
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">{config.title}</h1>
                        <p className="text-slate-400 text-sm">{config.description}</p>
                    </div>
                    {resolved.reference && (
                        <div className="bg-white/5 rounded-xl p-3">
                            <p className="text-xs text-slate-500">Referencia de pago</p>
                            <p className="text-sm font-mono text-slate-300 mt-1">{resolved.reference}</p>
                        </div>
                    )}
                    <button
                        onClick={() => { window.location.href = '/'; }}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white bg-white/10 hover:bg-white/20 transition-all border border-white/10"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Volver a Reveca
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PaymentResultPage;
